/**
 * The permission names the portal checks to decide what to render. These mirror the backend
 * catalogue, but the check here is UX only — the backend re-authorises every request
 * (spec section 65).
 */
export const Permissions = {
  paymentsView: 'payments.view',
  paymentsCreate: 'payments.create',
  paymentsCancel: 'payments.cancel',
  paymentsManualRecord: 'payments.manual.record',
  paymentsManualApprove: 'payments.manual.approve',
  paymentsInvestigate: 'payments.investigate',
  paymentsSyncProvider: 'payments.sync',

  paymentLinksView: 'paymentlinks.view',
  paymentLinksCreate: 'paymentlinks.create',
  paymentLinksExpire: 'paymentlinks.expire',

  customersView: 'customers.view',
  customersManage: 'customers.manage',
  customersAnonymise: 'customers.anonymise',

  refundsView: 'refunds.view',
  refundsRequest: 'refunds.request',
  refundsApprove: 'refunds.approve',
  refundsProcess: 'refunds.process',

  disputesView: 'disputes.view',
  disputesManage: 'disputes.manage',
  disputesApprove: 'disputes.approve',

  subscriptionsView: 'subscriptions.view',
  subscriptionsManage: 'subscriptions.manage',

  plansView: 'plans.view',
  plansManage: 'plans.manage',

  billingView: 'billing.view',
  billingManage: 'billing.manage',

  productsView: 'products.view',
  productsManage: 'products.manage',

  providersView: 'providers.view',
  providersManage: 'providers.manage',

  webhooksView: 'webhooks.view',
  webhooksReplay: 'webhooks.replay',

  eventsView: 'events.view',
  eventsReplay: 'events.replay',

  settlementsView: 'settlements.view',
  settlementsManage: 'settlements.manage',

  reconciliationView: 'reconciliation.view',
  reconciliationManage: 'reconciliation.manage',

  reportsView: 'reports.view',
  reportsExport: 'reports.export',

  usersView: 'users.view',
  usersManage: 'users.manage',
  rolesView: 'roles.view',
  rolesManage: 'roles.manage',

  auditView: 'audit.view',

  systemHealthView: 'system.health.view',
  settingsView: 'settings.view',
  settingsManage: 'settings.manage',
  jobsManage: 'jobs.manage',
} as const;

export type PermissionName = (typeof Permissions)[keyof typeof Permissions];

export const SUPER_ADMIN_ROLE = 'Super Admin';

/** True when the signed-in user holds the permission, or is a Super Admin. */
export function hasPermission(
  user: { permissions?: string[]; roles?: string[] } | null | undefined,
  permission: PermissionName,
): boolean {
  if (!user) {
    return false;
  }

  if (user.roles?.includes(SUPER_ADMIN_ROLE)) {
    return true;
  }

  return user.permissions?.includes(permission) ?? false;
}

/** True when the user holds at least one of the permissions. */
export function hasAnyPermission(
  user: { permissions?: string[]; roles?: string[] } | null | undefined,
  permissions: PermissionName[],
): boolean {
  return permissions.some((permission) => hasPermission(user, permission));
}
